/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

interface ComparisonCommit {
  id: string
  version: string
  created_at: string | null
}

export interface AdminComparison {
  id: string
  project_id: string
  from_commit: ComparisonCommit
  to_commit: ComparisonCommit
  created_at: string
  created_by: string
  has_activity_details: boolean
  activity_details_size_bytes: number
  summary: Record<string, any>
}

/**
 * Hook to fetch all comparisons for a specific project (admin only)
 */
export function useAdminProjectComparisons(projectId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'comparisons', projectId],
    queryFn: async () => {
      if (!projectId) return []

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/projects/${projectId}/comparisons`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Admin access required')
        }
        const errorText = await response.text()
        throw new Error(`Failed to fetch comparisons: ${response.status} - ${errorText}`)
      }

      const data = await response.json()
      return data as AdminComparison[]
    },
    enabled: !!projectId,
  })
}

/**
 * Hook to delete a comparison (admin only)
 */
export function useDeleteComparison() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (comparisonId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/comparisons/${comparisonId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to delete comparison: ${response.status} - ${errorText}`)
      }

      return await response.json()
    },
    onSuccess: (data) => {
      // Invalidate the comparisons list for this project
      queryClient.invalidateQueries({
        queryKey: ['admin', 'comparisons', data.project_id],
      })
    },
  })
}

/**
 * Hook to regenerate a comparison (admin only)
 */
export function useRegenerateComparison() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (comparisonId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/comparisons/${comparisonId}/regenerate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to regenerate comparison: ${response.status} - ${errorText}`)
      }

      return await response.json()
    },
    onSuccess: (data) => {
      // Invalidate the comparisons list for this project
      queryClient.invalidateQueries({
        queryKey: ['admin', 'comparisons', data.project_id],
      })
    },
  })
}
